import { useEffect, useState } from "react";

const TEAM_PRESENCE_API_URL = process.env.REACT_APP_TEAM_PRESENCE_API_URL || "/api/team-presence";
const TEAM_MEMBERS = ["FeaKH", "mcst3rx", "n1cho"];
const PUBLIC_STATUSES = new Set(["online", "idle", "dnd", "unavailable"]);
const REVALIDATE_INTERVAL_MS = 60_000;

function toTeamPresenceMap(data) {
  if (!Array.isArray(data?.members)) {
    return null;
  }

  const statusByName = new Map(
    data.members
      .filter((member) => TEAM_MEMBERS.includes(member?.name))
      .map((member) => [member.name, PUBLIC_STATUSES.has(member.status) ? member.status : "unavailable"]),
  );

  return Object.fromEntries(
    TEAM_MEMBERS.map((name) => [name, statusByName.get(name) || "unavailable"]),
  );
}

export function useTeamPresence() {
  const [teamPresence, setTeamPresence] = useState(null);

  useEffect(() => {
    const controller = new AbortController();
    let isMounted = true;

    async function loadTeamPresence() {
      try {
        const response = await fetch(TEAM_PRESENCE_API_URL, {
          signal: controller.signal,
          headers: { Accept: "application/json" },
          cache: "no-store",
        });

        if (!response.ok || !response.headers.get("content-type")?.includes("application/json")) {
          throw new Error("Team presence API request failed");
        }

        const presence = toTeamPresenceMap(await response.json());

        if (isMounted && presence) {
          setTeamPresence(presence);
        }
      } catch (error) {
        // Do not imply a team status when the private API is unavailable.
        if (error.name !== "AbortError" && isMounted) {
          setTeamPresence(null);
        }
      }
    }

    loadTeamPresence();
    const intervalId = window.setInterval(loadTeamPresence, REVALIDATE_INTERVAL_MS);

    return () => {
      isMounted = false;
      controller.abort();
      window.clearInterval(intervalId);
    };
  }, []);

  return teamPresence;
}
